import React, { FC, useEffect, useState } from 'react';
import { Badge, Button, Col, Dropdown, Row, Tooltip } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BellOutlined } from '@ant-design/icons';
import { ReactComponent as Exit } from '../../lib/img/exit.svg';
import LocalStorageService from '../../services/LocalStorageService';
import { useMainContext } from '../../containers/MainContainer/MainContext';
import './Navbar.less';
import { getFio } from '../../lib/utils/applications';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import NotificationsDropdownContainer from '../../containers/NotificationsDropdownContainer/NotificationsDropdownContainer';
import PersonPhoto from '../FileCard/PersonPhoto';
import axios from 'axios';
import { getPersonAvatarRoute } from '../../lib/routes/persons';
import { getPersonAvatar } from '../../store/slices/personsSlice/personsSlice';
import { checkIsLoading } from '../../lib/utils/checkLoadingStatus';

type MenuItem =
  | {
      name: 'Просьбы';
      route: 'applications/list';
      subRoute?: string;
      size: number;
    }
  | { name: 'Картотека'; route: 'files/list'; subRoute?: string; size: number }
  | {
      name: 'Администрирование';
      route: 'administration';
      subRoute?: string;
      size: number;
    }
  | { name: 'Инфо'; route: 'info'; subRoute?: string; size: number };

export const Navbar: FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess } = useMainContext();
  const { meUserFull, personAvatarLoading, personAvatar } = useAppSelector((state) => state.persons);
  const { personTypes } = useAppSelector((state) => state.dictionaries);
  const isUserNewVolunteer = meUserFull?.personStatusId === 1 && meUserFull?.personTypeId === 3;
  // @ts-ignore
  const { unreadNotificationsCount } = useAppSelector((state) => state.notifications);

  // Сохранение пути вкладки просьбы для возврата в карточку просьбы после перехода в другие разделы
  const [applicationsListSubRoute, setApplicationsListSubRoute] = useState<string>('');

  useEffect(() => {
    if (location.pathname.includes('applications')) {
      const formatLocation = location.pathname.replace('/applications/list/', '');
      setApplicationsListSubRoute(formatLocation === '/applications/list' ? '' : formatLocation);
    }
  }, [location]);

  useEffect(() => {
    if (meUserFull?.id) {
      dispatch(getPersonAvatar(meUserFull.id));
    }
  }, [meUserFull?.id]);

  const handleLogout = () => {
    LocalStorageService.clearToken();
    window.location.reload();
  };

  const menuData: MenuItem[] = [
    {
      name: 'Просьбы',
      route: 'applications/list',
      subRoute: applicationsListSubRoute,
      size: 85,
    },
    { name: 'Картотека', route: 'files/list', size: 92 },
    { name: 'Администрирование', route: 'administration', size: 162 },
    { name: 'Инфо', route: 'info', size: 61 },
  ];

  // Доступность пунктов меню
  const menuItemAccess = ({ route }: MenuItem): boolean => {
    switch (route) {
      case 'applications/list':
        // if (isUserNewVolunteer) return false;
        return true;
      case 'files/list':
        if (isUserNewVolunteer) return false;
        return hasAccess(['PERSONLIST.READ', 'PERSONLIST.READ.ALLREGIONS']);
      case 'administration':
        if (isUserNewVolunteer) return false;
        return hasAccess(['ADMINISTRATION.READ']);
      case 'info':
        return true;
    }
  };

  const getMenuItems = (): JSX.Element => {
    const section = location.pathname.split('/').filter(Boolean)[0];
    return (
      <div className="display-flex">
        {menuData.filter(menuItemAccess).map(({ route, name, size, subRoute }) => {
          const width = `${size}px`;
          const onClick = () => {
            navigate([route, subRoute].join('/'));
          };
          return (
            <div
              key={route}
              onClick={onClick}
              className="navbar-menu-item"
              style={
                route.split('/')[0] === section
                  ? {
                      width,
                      backgroundColor: 'white',
                      fontWeight: 'bold',
                    }
                  : { width }
              }
            >
              {name}
            </div>
          );
        })}
      </div>
    );
  };

  const [dropDownOpen, setDropDownOpen] = useState(false);

  const changeDropDown = (status: boolean): void => {
    setDropDownOpen(status);
  };

  return (
    <Row className="navbar navbar-menu ml20 mr20 pt20">
      <Col>
        <Tooltip title="На главную">
          <div className="logo-small mr20 cursor-pointer" onClick={() => navigate('/applications/list')}></div>
        </Tooltip>
      </Col>
      <Col className="display-flex" style={{ alignItems: 'center' }}>
        {getMenuItems()}
      </Col>
      <Col className="ml-auto mt1">
        {hasAccess(['APPLICATION.CREATE']) && !isUserNewVolunteer && (
          <Link to="/applications/create-application">
            <Button type="primary" style={{ height: 40 }}>
              Создать просьбу
            </Button>
          </Link>
        )}
      </Col>
      <Col>
        <Dropdown
          overlay={() => <NotificationsDropdownContainer changeDropDown={changeDropDown} />}
          trigger={['click']}
          open={dropDownOpen}
          onOpenChange={(e) => changeDropDown(e)}
          destroyPopupOnHide={true}
          className="ml22 mr22 mt11 font-size-20 "
        >
          <Badge dot={unreadNotificationsCount} color="rgba(238, 45, 36, 1)">
            <BellOutlined style={{ fontSize: '25px' }} className="dark-gray-color cursor-pointer" />
          </Badge>
        </Dropdown>
      </Col>
      <Col
        onClick={() => navigate('profile')}
        style={{
          cursor: 'pointer',
          lineHeight: '8px',
          height: 24,
          marginTop: 'auto',
          marginBottom: 'auto',
        }}
      >
        <div className="font-size-12">
          {getFio({
            firstName: meUserFull?.firstName,
            middleName: meUserFull?.middleName,
            lastName: meUserFull?.lastName,
          })}
        </div>
        <div className="font-size-12 light-gray-color mt8">
          {personTypes.find((item) => meUserFull?.personTypeId === item.id)?.name}
        </div>
      </Col>
      <Col className="ml15" onClick={() => navigate('profile/info')} style={{ cursor: 'pointer' }}>
        <PersonPhoto
          myProfile={true}
          image={personAvatar}
          mini={true}
          isLoading={checkIsLoading(personAvatarLoading)}
        />
      </Col>
      <Col>
        <Tooltip title="Выйти">
          <Exit className="mt10 ml20 cursor-pointer" onClick={handleLogout} />
        </Tooltip>
      </Col>
    </Row>
  );
};
